/**
 * Equalize product card title heights per visual row — Classic + Aligned only.
 * If any title in the row is 2 lines, match heights so price + ATC line up.
 * All 1-line rows stay tight (no gap).
 * - Grid: group cards by shared top position.
 * - Swiper: all visible slides in a wrapper = one row.
 */
(() => {
  const CARD = '.product-item.quick-action-below.size-atc-btn-below-content.size-atc-align-aligned';
  const TITLE = '.product__name';
  let resizeTimer = null;
  let scheduled = false;
  let running = false;

  function titleEl(card) {
    return card.querySelector(TITLE);
  }

  function isVisible(el) {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  }

  function cardsInParent(parent) {
    const direct = [...parent.children].filter(
      (el) => el.matches && el.matches(CARD) && isVisible(el)
    );
    if (direct.length) return direct;

    return [...parent.querySelectorAll(`.swiper-slide${CARD}, .swiper-slide > ${CARD}`)].filter(
      (card, index, list) => list.indexOf(card) === index && isVisible(card)
    );
  }

  function resetTitles(cards) {
    cards.forEach((card) => {
      const title = titleEl(card);
      if (title) title.style.minHeight = '';
    });
  }

  function applyMaxHeight(cards) {
    if (cards.length < 2) {
      resetTitles(cards);
      return;
    }

    resetTitles(cards);

    const titles = cards.map(titleEl).filter(Boolean);
    if (!titles.length) return;

    let maxHeight = 0;
    let minHeight = Infinity;
    titles.forEach((title) => {
      const h = title.getBoundingClientRect().height;
      maxHeight = Math.max(maxHeight, h);
      minHeight = Math.min(minHeight, h);
    });

    if (maxHeight <= 1) return;

    // Only reserve gap when the row has mixed heights (2-line next to 1-line).
    if (maxHeight - minHeight < 4) return;

    const value = `${Math.ceil(maxHeight)}px`;
    titles.forEach((title) => {
      title.style.minHeight = value;
    });
  }

  function groupByRow(cards) {
    const rows = [];
    cards.forEach((card) => {
      const top = Math.round(card.getBoundingClientRect().top);
      let row = rows.find((r) => Math.abs(r.top - top) <= 12);
      if (!row) {
        row = { top, cards: [] };
        rows.push(row);
      }
      row.cards.push(card);
    });
    return rows;
  }

  function equalize() {
    if (running) return;
    running = true;

    try {
      const parents = new Set();
      document.querySelectorAll(CARD).forEach((card) => {
        if (!isVisible(card)) return;
        const parent = card.closest('.swiper-wrapper, #product-grid, .grid-cols');
        if (parent) parents.add(parent);
        else if (card.parentElement) parents.add(card.parentElement);
      });

      if (!parents.size) return;

      parents.forEach((parent) => {
        const hiddenPanel = parent.closest(
          '.product-tabs__content-item:not(.active), [hidden], .d-none'
        );
        if (hiddenPanel) return;

        const targets = cardsInParent(parent);
        if (!targets.length) return;

        if (parent.classList.contains('swiper-wrapper')) {
          applyMaxHeight(targets);
          return;
        }

        groupByRow(targets).forEach((row) => applyMaxHeight(row.cards));
      });
    } finally {
      running = false;
    }
  }

  function scheduleEqualize(delay = 0) {
    if (delay > 0) {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        scheduled = false;
        requestAnimationFrame(() => equalize());
      }, delay);
      return;
    }
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      equalize();
    });
  }

  function onResize() {
    scheduleEqualize(120);
  }

  document.addEventListener('DOMContentLoaded', () => scheduleEqualize());
  window.addEventListener('load', () => scheduleEqualize(200));
  window.addEventListener('resize', onResize);
  document.addEventListener('shopify:section:load', () => scheduleEqualize(50));
  document.addEventListener('shopify:section:reorder', () => scheduleEqualize(50));
  window.addEventListener('collection-filters:navigated', () => scheduleEqualize(50));
  window.addEventListener('card-row-align', () => scheduleEqualize(30));

  document.addEventListener('click', (event) => {
    if (!event.target.closest('.product-tabs__header-item-js, .product-tabs__header-item')) {
      return;
    }
    // One delayed pass after the tab panel finishes swapping.
    scheduleEqualize(320);
  });

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => scheduleEqualize(50)).catch(() => {});
  }

  if (typeof MutationObserver !== 'undefined') {
    const grid = document.getElementById('product-grid');
    if (grid) {
      const observer = new MutationObserver(() => scheduleEqualize(60));
      observer.observe(grid, { childList: true });
    }

    document.querySelectorAll('suitable-finder, product-tabs').forEach((root) => {
      const observer = new MutationObserver((mutations) => {
        const tabChanged = mutations.some(
          (m) =>
            m.type === 'attributes' &&
            m.target.classList &&
            (m.target.classList.contains('product-tabs__content-item') ||
              m.target.classList.contains('product-tabs__header-item') ||
              m.target.classList.contains('product-tabs__header-item-js'))
        );
        if (tabChanged) scheduleEqualize(80);
      });
      observer.observe(root, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }
})();
